import { Injectable, signal, inject } from '@angular/core';
import { IInstructions } from '../../automation/interfaces/i-instructions';
import { IDocument } from '../../../interfaces/i-document';
import { IAgent } from '../../../interfaces/i-agents';
import { ISensitivityLabel } from '../../../interfaces/i-sensitivity-label';
import { UtilitiesService } from '../../../services/utilities.service';
import { IConversation } from "../interfaces/i-conversation";
import { ChatDocumentService } from './chat-document.service';
import { ChatSessionService } from './chat-session.service';
import { SENSITIVITY_LABELS_FLAT } from '../../../constants/sensitivity.constants';

@Injectable({
  providedIn: 'root'
})
export class ConversationStarterService {
  // State of whether we are in the Start Conversation page or not
  isStartConversation = signal(false);
  hasError = signal(false);
  errorMessages = signal<string[]>([]);
  /**
   * Tracks the IDs of agents that are incompatible with the currently selected
   * sensitivity label. Components can read this to highlight affected agent chips.
   * Reset to an empty Set whenever the error state is cleared.
   */
  incompatibleAgentIds = signal<Set<string>>(new Set());
  /**
   * Single source of truth for whether the user has explicitly chosen a
   * sensitivity label via the label picker.
   *
   * - Set to `true` inside `updateSensitivityLabel` (the only entry-point for
   *   user-driven label changes).
   * - Reset to `false` by `setSensitivityLabelByAgents` (system auto-derives
   *   the label) and when the component is destroyed or resets.
   *
   * Used by `onAgentSelected` to decide which validation path to take:
   * - `true`  → keep the user's label and re-validate agents against it via
   *             `updateSensitivityLabel`.
   * - `false` → auto-derive the best common label via `setSensitivityLabelByAgents`.
   */
  userHasChosenSensitivityLabel = signal<boolean>(false);
  // Hacky fix for file attachments being set to an empty array when leaving the Start Session page and creating a session
  // due to the ChatDocumentService clearing selected documents on session creation.
  // this is bc when we create a session, we do a check for if we have sessionId and if !isStartConversation, we use the sessionInputData map to set documents,
  // if it's empty, then we lose the selected documents.
  // once we set the sessionInputData map to use the ChatDocumentService selectedDocuments(), we can set the flag to false.
  isSessionCreatedFromStartConversation = signal(false);

  // Hacky fix for setting the sensitivity label on a newly created session when we have a File Upload Instruction from the Start Session page
  setSensitivityLabelByAttachedDocuments = signal(false);

  conversation = signal<IConversation | undefined>(undefined);


  #utilitiesService = inject(UtilitiesService);
  #chatDocumentService = inject(ChatDocumentService);
  #chatSessionService = inject(ChatSessionService);

  setSensitivityLabelByAgents(agents: IAgent[]) {
    // Reset the error state
    this.hasError.set(false);
    this.errorMessages.set([]);
    this.incompatibleAgentIds.set(new Set());
    // System is auto-deriving the label, so user has not chosen it
    this.userHasChosenSensitivityLabel.set(false);

    // if we remove all agents, we don't do anything
    if (!Array.isArray(agents) || agents.length === 0) {
      return;
    }

    // Collect SL ID arrays for each agent
    const labelIdArrays: number[][] = agents.map(agent =>
      agent.details?.sensitivityLabels?.map(label => label.id) ?? []
    );

    // Find the common sensitivity label IDs (intersection)
    const commonIds = labelIdArrays.reduce((common, current) =>
      common.filter(id => current.includes(id))
    );

    // If there are common labels, select the lowest one
    if (commonIds.length > 0) {
      // Sort in ascending order
      commonIds.sort((a, b) => a - b);
      let lowestId = commonIds[0];

      // If there are selected documents, ensure we do not set a lower SL than the current one,
      // this is to avoid downgrading the SL when documents are attached
      if (this.#chatDocumentService.selectedDocuments().length > 0) {
        const currentSensitivityLabelId = this.conversation()?.sensitivityLabel!.id;

        let noCommonSensitivityLabelFound = true;
        for (const id of commonIds) {
          if (id >= currentSensitivityLabelId!) {
            lowestId = id;
            noCommonSensitivityLabelFound = false;
            break;
          } else {
            noCommonSensitivityLabelFound = true;
          }
        }

        if (noCommonSensitivityLabelFound) {
          // No common denominator found that is equal to or higher than the current SL
          this.hasError.set(true);
          this.errorMessages.update(errors => [...errors, `The selected Agent(s) has a Sensitivity Label that is lower than the currently attached document's Sensitivity Label.
            \nPlease either adjust the attached document(s) or your Agent(s) selection.`]);
          return;
        }
      }

      const labels = this.#utilitiesService.findLabelsByIds([lowestId]);
      if (labels.length > 0) {
        this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: labels[0] } : convo);
        return;
      }
    } else {
      // No common denominator found - identify which agents are incompatible
      // Strategy: Find the most common label range among agents, then identify outliers

      // Count how many agents share each label ID
      const labelFrequency = new Map<number, IAgent[]>();
      for (const agent of agents) {
        const agentLabelIds = agent.details?.sensitivityLabels?.map(l => l.id) ?? [];
        for (const labelId of agentLabelIds) {
          const agentsWithLabel = labelFrequency.get(labelId) ?? [];
          labelFrequency.set(labelId, [...agentsWithLabel, agent]);
        }
      }

      // Find the maximum shared count
      const maxSharedCount = Math.max(
        ...Array.from(labelFrequency.values()).map(agentList => agentList.length)
      );

      // Find all label IDs that have the maximum shared count
      const mostSharedLabelIds = Array.from(labelFrequency.entries())
        .filter(([, agentList]) => agentList.length === maxSharedCount)
        .map(([labelId]) => labelId);

      // Agents are incompatible if they don't have ANY of the most shared labels
      const incompatibleAgents = agents.filter(agent => {
        const agentLabelIds = agent.details?.sensitivityLabels?.map(l => l.id) ?? [];
        return !mostSharedLabelIds.some(labelId => agentLabelIds.includes(labelId));
      });

      if (incompatibleAgents.length > 0) {
        const agentNames = incompatibleAgents
          .map(a => a.details?.name ?? a.id)
          .join(', ');

        this.hasError.set(true);
        this.errorMessages.set([
          `The selected Agents do not have a common Sensitivity Label. The following Agent(s) are incompatible: ${agentNames}.`
        ]);
        this.incompatibleAgentIds.set(new Set(incompatibleAgents.map(a => a.id)));
      } else {
        // Fallback: all agents are somehow incompatible with each other
        const agentNames = agents.map(a => a.details?.name ?? a.id).join(', ');
        this.hasError.set(true);
        this.errorMessages.set([
          `The selected Agents do not have a common Sensitivity Label: ${agentNames}.`
        ]);
        this.incompatibleAgentIds.set(new Set(agents.map(a => a.id)));
      }
    }
  }

  /**
   * Sets the sensitivity label for the conversation based on the documents attached.
   * Finds the document with the highest sensitivity label and updates the conversation or session accordingly.
   * @param documents The list of documents to check.
   */
  setSensitivityLabelByDocuments(documents: IDocument[]) {
    const highestSensitivityLabelDocument = documents.find(doc => doc.sensitivityLabel?.id === Math.max(...documents.map(d => d.sensitivityLabel?.id ?? 0)));

    if (highestSensitivityLabelDocument && highestSensitivityLabelDocument.sensitivityLabel && highestSensitivityLabelDocument.sensitivityLabel.guid) {
      if (this.setSensitivityLabelByAttachedDocuments()) {
        const labels = this.#utilitiesService.findLabelsByGuids(highestSensitivityLabelDocument.sensitivityLabel ? [highestSensitivityLabelDocument.sensitivityLabel.guid!] : []);
        if (labels[0].id >= this.#chatSessionService.selectedSession()!.sensitivityLabels![0]?.id!) {
          this.#chatSessionService.selectedSession()!.sensitivityLabels = labels;
        }
        return;
      }

      // we set the default right operator value to 2 since that is (Public) and is the default sensitivity label of the start conversation page
      if (this.isStartConversation() && this.conversation()?.sensitivityLabel && highestSensitivityLabelDocument.sensitivityLabel!.id > this.conversation()!.sensitivityLabel!.id) {
        const labels = this.#utilitiesService.findLabelsByGuids(highestSensitivityLabelDocument.sensitivityLabel ? [highestSensitivityLabelDocument.sensitivityLabel.guid!] : []);
        if (labels.length > 0) {
          this.conversation.update((convo) => (convo ? { ...convo, sensitivityLabel: labels[0] } : convo));
          return;
        }
      }
    } else {
      // TODO commenting this out for now
      // this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: this.tempSensitivityLabels() } : convo);
      return;
    }
  }

  setSensitivityLabelByInstruction(instruction: IInstructions) {
    const labels = instruction.details?.sensitivityLabels!;

    this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: labels[0] } : convo);
  }

  /**
   * Updates the sensitivity label for the conversation after validating against selected agents and attached documents.
   * @param label The new sensitivity label to set.
   * @param agents The list of selected agents to validate against.
   * @returns void
   */
  updateSensitivityLabel(label: ISensitivityLabel, agents: IAgent[]) {
    // Mark that the user has explicitly chosen a sensitivity label.
    // This flag persists even if errors clear, ensuring consistent routing logic
    // in onAgentSelected/removeAgent.
    this.userHasChosenSensitivityLabel.set(true);

    const newErrors: string[] = [];
    // Collect agents whose sensitivity labels do not include the chosen label.
    const incompatibleAgents: IAgent[] = [];

    // Check if label is allowed by all selected agents
    if (Array.isArray(agents) && agents.length > 0) {
      for (const agent of agents) {
        const isAllowed = agent.details?.sensitivityLabels?.some(l => l.id === label.id) ?? false;
        if (!isAllowed) {
          incompatibleAgents.push(agent);
        }
      }

      if (incompatibleAgents.length > 0) {
        const agentNames = incompatibleAgents
          .map(a => a.details?.name ?? a.id)
          .join(', ');
        newErrors.push(
          `The selected Sensitivity Label is not compatible with the following Agent(s): ${agentNames}.\n`
        );
      }
    }

    // Persist the set of incompatible agent IDs so the template can highlight chips.
    this.incompatibleAgentIds.set(new Set(incompatibleAgents.map(a => a.id)));

    // Check if label is valid for attached documents
    const documents = this.#chatDocumentService.selectedDocuments();
    if (documents.length > 0) {
      const highestDocId = documents.reduce((max, doc) => {
        let docId = doc.sensitivityLabel?.id ?? 0;

        // Find if the document label has a parent ID
        const docLabel = SENSITIVITY_LABELS_FLAT.find(l => l.id === docId);
        if (docLabel?.parentId) {
          docId = docLabel.parentId;
        }

        return docId > max ? docId : max;
      }, -1);

      const labelIdWithParent = SENSITIVITY_LABELS_FLAT.find(l => l.id === label.id)?.parentId ?? label.id;

      if (labelIdWithParent < highestDocId) {
        newErrors.push(`The selected Sensitivity Label is lower than the currently attached document's Sensitivity Label.\n`);
      }
    }

    // Update conversation sensitivity label, regardless of errors.
    // User setting the SL takes top priority.
    this.conversation.update(convo => convo ? { ...convo, sensitivityLabel: label } : convo);

    if (newErrors.length > 0) {
      this.hasError.set(true);
      this.errorMessages.set(newErrors);
    } else {
      this.hasError.set(false);
      this.errorMessages.set([]);
      this.incompatibleAgentIds.set(new Set());
    }
  }
}
